import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';

import useMessageQueues from '../../../hooks/useMessageQueues';
import { isAxiosError } from 'axios';
import MessageQueuesTable from '../../../components/MessageQueuesTable/MessageQueuesTable';
import { GetMessageQueuesQuery } from '@hud/portal-dtos/webapp-message-queue.dto';
import Page from '../../../components/Page/Page';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useAnalytics from '../../../hooks/useAnalytics';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import { useMessageQueuesFilters } from '../../../hooks/useMessageQueuesFilter';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import { ThresholdFilter } from '@hud/portal-dtos/webapp-common.dto';
import {
  CommonThresholdQueryParams,
  getThresholdFilter,
  ThresholdFilterKey,
} from '../../../utils';
import { Events } from '../../../analytics';

type MessageQueuesPageQueryParams = CommonTableQueryParams &
  CommonThresholdQueryParams & {
    services: number[];
  };

export default function MessageQueuesPage() {
  const analytics = useAnalytics();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const [totalPages, setTotalPages] = useState<number>(0);
  const messageQueuesFilters = useMessageQueuesFilters();

  const referralBreadcrumbs: BreadcrumbItem[] | undefined = useMemo(() => {
    if (!messageQueuesFilters) return;
    return [
      {
        title: 'Message Queues',
        to: `/explore/messageQueues?${messageQueuesFilters}`,
        nowrap: true,
      },
    ];
  }, [messageQueuesFilters]);

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<MessageQueuesPageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'normalizedRuns.value',
      desc: true,
      services: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  const { userPreferredServices } = useUserPreferences();
  useEffect(() => {
    if (pageState.services.length === 0) {
      setPageState({ services: userPreferredServices ?? [] });
    }
  }, [userPreferredServices]);

  const clearAllFilters = () => {
    setPageState({
      services: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });
  };

  const handleFilteredServicesChange = (value: number[]) => {
    setPageState({ services: value, page: 1 });
  };

  const handleThresholdFilterChange = (
    key: ThresholdFilterKey,
    value: ThresholdFilter | undefined,
    eventName: keyof Events,
  ) => {
    setPageState({
      [key]: value?.value,
      [`${key}_op`]: value?.operator,
      page: 1,
    });

    analytics.capture(eventName, {
      environment: environment?.name,
      toggle_value: !!value,
      filter_value: value?.value ?? 0,
      filter_operator: value?.operator ?? 'gte',
    });
  };

  const handleFilteredDurationAvgChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'durationAvg',
      value,
      'message_queues_duration_avg_filter_toggled',
    );
  };

  const handleFilteredNormalizedRunsChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'normalizedRuns',
      value,
      'message_queues_normalized_runs_filter_toggled',
    );
  };

  const handleFilteredErrorRateChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'errorRate',
      value,
      'message_queues_error_rate_filter_toggled',
    );
  };

  const messageQueuesQueryParams = useMemo<GetMessageQueuesQuery>(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetMessageQueuesQuery['orderBy'],
      timeFrame,
      environmentName: environment?.name,
      serviceIds: pageState.services,
      durationAvg: getThresholdFilter(pageState.durationAvg, pageState.durationAvg_op),
      normalizedRuns: getThresholdFilter(
        pageState.normalizedRuns,
        pageState.normalizedRuns_op,
      ),
      errorRate: getThresholdFilter(pageState.errorRate, pageState.errorRate_op),
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      timeFrame,
      environment?.name,
      pageState.services,
      pageState.durationAvg,
      pageState.durationAvg_op,
      pageState.normalizedRuns,
      pageState.normalizedRuns_op,
      pageState.errorRate,
      pageState.errorRate_op,
    ],
  );
  const [debouncedMessageQueuesQueryParams] = useDebouncedValue(
    messageQueuesQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );
  const messageQueuesQuery = useMessageQueues(debouncedMessageQueuesQueryParams);

  useEffect(() => {
    document.title = 'Message Queues • Hud';
  }, []);
  useEffect(() => {
    if (messageQueuesQuery.isSuccess) {
      setTotalPages(Math.ceil(messageQueuesQuery.data.countFiltered / DEFAULT_PAGE_SIZE));
    }
  }, [messageQueuesQuery.data?.total, messageQueuesQuery.isSuccess]);

  // Analytics
  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('message_queues_viewed', {
      environment: environment?.name,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environment?.name]);

  useEffect(() => {
    if (!environment?.name) return;
    analytics.capture('message_queues_searched', {
      search: tableState.search,
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.search]);

  useEffect(() => {
    if (messageQueuesQuery.isSuccess) {
      analytics.capture('message_queues_paginated', {
        page: tableState.page,
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        search: tableState.search,
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.page]);

  useEffect(() => {
    if (messageQueuesQuery.isSuccess) {
      analytics.capture('message_queues_sorted', {
        column_name: tableState.orderBy?.key ?? '',
        column_order: tableState.orderBy?.sortOrder ?? 'asc',
        environment: environment?.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState.orderBy.key]);

  if (messageQueuesQuery.isError) {
    console.log(messageQueuesQuery.error);
    if (!isAxiosError(messageQueuesQuery.error)) {
      return (
        <div>
          <div>Something went wrong</div>
        </div>
      );
    }
    const error = messageQueuesQuery.error.response?.data;
    return <div>{error?.message ?? 'Something went wrong'}</div>;
  }

  return (
    <Page
      header="Message Queues"
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <MessageQueuesTable
        timeFrame={timeFrame}
        fullPageTable
        queues={messageQueuesQuery.data?.messageQueues ?? []}
        loading={messageQueuesQuery.isLoading}
        total={messageQueuesQuery.data?.total}
        filtered={messageQueuesQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={totalPages}
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        filteredServices={pageState.services}
        onFilteredServicesChange={handleFilteredServicesChange}
        referralBreadcrumbs={referralBreadcrumbs}
        filteredDurationAvg={getThresholdFilter(
          pageState.durationAvg,
          pageState.durationAvg_op,
        )}
        onFilteredDurationAvgChange={handleFilteredDurationAvgChange}
        filteredNormalizedRuns={getThresholdFilter(
          pageState.normalizedRuns,
          pageState.normalizedRuns_op,
        )}
        onFilteredNormalizedRunsChange={handleFilteredNormalizedRunsChange}
        filteredErrorRate={getThresholdFilter(
          pageState.errorRate,
          pageState.errorRate_op,
        )}
        onFilteredErrorRateChange={handleFilteredErrorRateChange}
        onClearAllFilters={clearAllFilters}
      />
    </Page>
  );
}
